import { PageHeader } from "@/components/dashboard/page-header";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { DEFAULT_PIPELINE, type StageDef, type Tier, makeStageKey } from "@ow/shared";
import { Link } from "@tanstack/react-router";
import {
  Check,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Lock,
  Plus,
  RotateCcw,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { useState } from "react";

const TIERS: { value: Tier; label: string }[] = [
  { value: "csuite", label: "C-Suite" },
  { value: "vp_director", label: "VP / Director" },
  { value: "general", label: "General" },
];
const MAX_STAGES = 12;

/** Same ladder validation the launch flow uses — a pipeline needs ≥1 stage, every stage named, names unique. */
function validate(stages: StageDef[]): string | null {
  if (stages.length === 0) return "A pipeline needs at least one stage.";
  if (stages.some((s) => !s.label.trim())) return "Every stage needs a name.";
  const seen = new Set<string>();
  for (const s of stages) {
    const l = s.label.trim().toLowerCase();
    if (seen.has(l)) return `Two stages are both named “${s.label.trim()}”. Stage names must be unique.`;
    seen.add(l);
  }
  return null;
}

function sameLadder(a: StageDef[], b: StageDef[]): boolean {
  return a.length === b.length && a.every((s, i) => s.key === b[i]?.key && s.label === b[i]?.label);
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <div className="field-label mb-1.5">{children}</div>;
}

/** Segmented single-select in the bar's raised-chip language — used for Tier (three fixed options read
 *  better inline than in a dropdown). */
function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="inline-flex rounded-lg bg-muted p-0.5">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className={cn(
              "rounded-md px-3 py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
              active
                ? "bg-card text-foreground shadow-sm ring-1 ring-border"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Controlled, local pipeline editor — the same add / rename / reorder / remove interactions as the
 *  post-create configurator (drag handle AND up/down for keyboard), but held in the create page's state
 *  so the whole role is set up in one flow. Seeded from the default ladder, never a blank list. */
function PipelineEditor({ stages, onChange }: { stages: StageDef[]; onChange: (next: StageDef[]) => void }) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const error = validate(stages);

  const rename = (i: number, label: string) => onChange(stages.map((s, j) => (j === i ? { ...s, label } : s)));
  const remove = (i: number) => onChange(stages.filter((_, j) => j !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    const a = stages[i];
    const b = stages[j];
    if (!a || !b) return;
    const next = [...stages];
    next[i] = b;
    next[j] = a;
    onChange(next);
  };
  const add = () =>
    onChange([
      ...stages,
      {
        key: makeStageKey(
          "New stage",
          stages.map((s) => s.key),
        ),
        label: "New stage",
      },
    ]);
  const reorder = (from: number, to: number) => {
    if (from === to) return;
    const next = [...stages];
    const [moved] = next.splice(from, 1);
    if (!moved) return;
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <ol className="divide-y overflow-hidden rounded-lg border">
        {stages.map((s, i) => {
          const isOver = overIdx === i && dragIdx !== null && dragIdx !== i;
          return (
            <li
              key={s.key}
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragEnter={() => setOverIdx(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragIdx !== null) reorder(dragIdx, i);
                setDragIdx(null);
                setOverIdx(null);
              }}
              onDragEnd={() => {
                setDragIdx(null);
                setOverIdx(null);
              }}
              className={cn(
                "flex items-center gap-2 bg-card px-2.5 py-2 transition-colors",
                dragIdx === i && "opacity-50",
                isOver && "bg-accent",
              )}
            >
              <button
                type="button"
                aria-label="Drag to reorder"
                className="cursor-grab touch-none text-muted-foreground/50 hover:text-foreground active:cursor-grabbing"
              >
                <GripVertical className="size-4" />
              </button>
              <span className="grid size-6 shrink-0 place-items-center rounded-full bg-muted text-[11px] tabular-nums text-muted-foreground">
                {i + 1}
              </span>
              <Input
                value={s.label}
                onChange={(e) => rename(i, e.target.value)}
                aria-label={`Stage ${i + 1} name`}
                className="h-9 flex-1"
              />
              <div className="flex shrink-0 items-center">
                <button
                  type="button"
                  aria-label="Move up"
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                  className="grid size-8 place-items-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-30"
                >
                  <ChevronUp className="size-4" />
                </button>
                <button
                  type="button"
                  aria-label="Move down"
                  disabled={i === stages.length - 1}
                  onClick={() => move(i, 1)}
                  className="grid size-8 place-items-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-30"
                >
                  <ChevronDown className="size-4" />
                </button>
                <button
                  type="button"
                  aria-label="Remove stage"
                  disabled={stages.length <= 1}
                  onClick={() => remove(i)}
                  className="grid size-8 place-items-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-30"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            </li>
          );
        })}
      </ol>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={add}
          disabled={stages.length >= MAX_STAGES}
          className="inline-flex items-center gap-1.5 rounded-md border bg-card px-2.5 py-1.5 text-sm font-medium text-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
        >
          <Plus className="size-4" /> Add stage
        </button>
        <button
          type="button"
          onClick={() => onChange(DEFAULT_PIPELINE)}
          disabled={sameLadder(stages, DEFAULT_PIPELINE)}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
        >
          <RotateCcw className="size-3.5" /> Reset to default
        </button>
        <span className="eyebrow ml-auto">
          {stages.length} stage{stages.length === 1 ? "" : "s"}
        </span>
      </div>

      {error ? (
        <p className="flex items-center gap-1.5 text-xs text-[var(--color-warning)]">
          <TriangleAlert className="size-3.5" /> {error}
        </p>
      ) : null}
    </div>
  );
}

/** Full-page "New role" create flow — role facts AND the interview pipeline in ONE surface (best-practice
 *  for object-with-sub-configuration: a modal can't hold a growable stage list). Ends in a deliberate
 *  choice: Publish now (client sees it, pipeline locks) or Save as draft (keep private, finish later),
 *  mirroring the near-universal ATS draft→publish model. Session-local in the design mock. */
export function NewRolePage() {
  const [title, setTitle] = useState("");
  const [tier, setTier] = useState<Tier>("csuite");
  const [fn, setFn] = useState("");
  const [owner, setOwner] = useState("");
  const [budgetMin, setBudgetMin] = useState("");
  const [budgetMax, setBudgetMax] = useState("");
  const [notes, setNotes] = useState("");
  const [stages, setStages] = useState<StageDef[]>(DEFAULT_PIPELINE);
  const [done, setDone] = useState<{ title: string; status: "published" | "draft"; stageCount: number } | null>(null);

  const pipelineError = validate(stages);
  const canSubmit = title.trim().length > 0 && !pipelineError;

  const submit = (status: "published" | "draft") => {
    if (!canSubmit) return;
    setDone({ title: title.trim(), status, stageCount: stages.length });
  };

  const reset = () => {
    setTitle("");
    setTier("csuite");
    setFn("");
    setOwner("");
    setBudgetMin("");
    setBudgetMax("");
    setNotes("");
    setStages(DEFAULT_PIPELINE);
    setDone(null);
  };

  return (
    <div className="animate-fade-up space-y-6">
      <PageHeader
        title="New role"
        description="Set up the role and the stages candidates move through — then publish it to the client, or save it as a draft and finish later."
      />

      {done ? (
        <DonePanel done={done} onCreateAnother={reset} />
      ) : (
        <>
          {/* Role details */}
          <section className="space-y-4 rounded-xl border bg-card p-5">
            <div className="eyebrow">Role details</div>
            <div>
              <FieldLabel>Role title</FieldLabel>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Chief Marketing Officer"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel>Tier</FieldLabel>
                <Segmented value={tier} onChange={setTier} options={TIERS} />
              </div>
              <div>
                <FieldLabel>Function</FieldLabel>
                <Input value={fn} onChange={(e) => setFn(e.target.value)} placeholder="e.g. Marketing" />
              </div>
              <div>
                <FieldLabel>Search lead</FieldLabel>
                <Input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="e.g. Alex Morgan" />
              </div>
              <div>
                <FieldLabel>
                  Budget range <span className="font-normal normal-case text-muted-foreground">· optional</span>
                </FieldLabel>
                <div className="flex items-center gap-2">
                  <Input
                    value={budgetMin}
                    onChange={(e) => setBudgetMin(e.target.value)}
                    inputMode="decimal"
                    placeholder="Min"
                  />
                  <span className="text-muted-foreground">–</span>
                  <Input
                    value={budgetMax}
                    onChange={(e) => setBudgetMax(e.target.value)}
                    inputMode="decimal"
                    placeholder="Max"
                  />
                </div>
              </div>
            </div>
            <div>
              <FieldLabel>
                Notes <span className="font-normal normal-case text-muted-foreground">· optional</span>
              </FieldLabel>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Mandate context, must-haves, anything the team should know."
              />
            </div>
          </section>

          {/* Pipeline */}
          <section className="space-y-3 rounded-xl border bg-card p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="eyebrow">Pipeline</div>
              <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Lock className="size-3" /> Locks when you publish
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              The stages every candidate moves through. Reorder, rename, add or remove them now.
              <span className="text-foreground"> Once you publish, the pipeline is locked</span> — candidates are
              tracked against these stages for the life of the search.
            </p>
            <PipelineEditor stages={stages} onChange={setStages} />
          </section>

          {/* Action bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
            <Link
              to={"/admin-view" as never}
              className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            >
              Cancel
            </Link>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => submit("draft")}
                disabled={!canSubmit}
                className="inline-flex h-9 items-center rounded-md border bg-card px-3.5 text-sm font-medium text-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
              >
                Save as draft
              </button>
              <button
                type="button"
                onClick={() => submit("published")}
                disabled={!canSubmit}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-foreground px-3.5 text-sm font-medium text-background outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
              >
                <Check className="size-4" /> Publish role
              </button>
            </div>
          </div>
          <p className="text-right text-[11px] text-muted-foreground">
            Publishing makes the role visible to the client. Preview only — not saved yet.
          </p>
        </>
      )}
    </div>
  );
}

/** Post-create confirmation — states exactly what happened (live vs private) and the consequence, then
 *  offers the two natural next moves. */
function DonePanel({
  done,
  onCreateAnother,
}: {
  done: { title: string; status: "published" | "draft"; stageCount: number };
  onCreateAnother: () => void;
}) {
  const published = done.status === "published";
  return (
    <div className="space-y-4 rounded-xl border bg-card p-8 text-center">
      <span className="mx-auto grid size-11 place-items-center rounded-full bg-[var(--brand-accent)]/20 text-foreground">
        <Check className="size-5" />
      </span>
      <div className="space-y-1.5">
        <h2 className="text-lg font-semibold tracking-tight text-foreground">
          “{done.title}” {published ? "is live" : "saved as a draft"}
        </h2>
        <p className="mx-auto max-w-md text-sm text-muted-foreground">
          {published ? (
            <>
              The client can now see this role. Its {done.stageCount}-stage pipeline is locked — candidates move through
              these stages for the life of the search.
            </>
          ) : (
            <>
              Only your team can see it for now. Open it whenever you're ready to finish setup and publish it to the
              client.
            </>
          )}
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
        <Link
          to={"/admin-view" as never}
          className="inline-flex h-9 items-center rounded-md bg-foreground px-3.5 text-sm font-medium text-background outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring"
        >
          Back to overview
        </Link>
        <button
          type="button"
          onClick={onCreateAnother}
          className="inline-flex h-9 items-center gap-1.5 rounded-md border bg-card px-3.5 text-sm font-medium text-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Plus className="size-4" /> Create another
        </button>
      </div>
    </div>
  );
}
